/* iceberg-ui */
import { IconName, IconWithVariant } from '@auviknetworks/iceberg-ui';

/* MUI */
import {
    Divider,
    Menu as MUIMenu,
    MenuItem as MUIMenuItem,
    MenuItemProps,
    MenuList,
    MenuProps,
    Typography
} from '@mui/material';

/*  Shared  */
import { FontSizes } from 'application/Shared/Constants';

/* React */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * ItemBase is the base type for all items in the menu.
 */
export interface ItemBase {
    /**
     * If true, a divider will be added after the item.
     */
    divider?: boolean;
    /**
     * The key to use for the translation of the label.
     */
    labelKey: string;
    /**
     * The type of the item.
     */
    type: 'header' | 'leaf' | 'submenu';
    /**
     * Optional prefix Icon name
     */
    prefix?: IconName;
    /**
     * Optional suffix Icon name.
     */
    suffix?: IconName;
}

export interface Header extends ItemBase {
    type: 'header';
}

export interface Leaf extends ItemBase {
    type: 'leaf';
    action?: <HTMLElement>(event: React.MouseEvent<HTMLElement>) => void;
    dataTrackid?: string;
    disabled?: boolean;
}

export interface Submenu extends ItemBase {
    type: 'submenu';
    submenu: Item[] | undefined;
    dataTrackid?: string;
}

export type Item = Header | Leaf | Submenu;

/**
 * Props for the enhanced Menu component.
 *
 * @interface EnhancedMenuProps
 * @extends {MenuProps}
 */
interface EnhancedMenuProps extends MenuProps {
    /**
     * Callback fired when the menu is closed.
     *
     * @param {React.MouseEvent<HTMLElement>} event - The event source of the callback.
     * @param {'backdropClick' | 'escapeKeyDown'} reason - The reason for closing the menu.
     */
    onClose: (
        event: React.MouseEvent<HTMLElement>,
        reason: 'backdropClick' | 'escapeKeyDown'
    ) => void;

    /**
     * An array of menu items to be displayed.
     */
    menu: Item[];

    /**
     * Optional callback to control the visibility of the parent menu.
     *
     * @param {boolean} keep - Whether to keep the parent menu open.
     */
    keepParent?: (keep: boolean) => void;
}

interface EnhancedMenuItemProps extends MenuItemProps {
    /**
     * Optional prefix Icon name.
     */
    prefix?: string;

    /**
     * Optional suffix Icon name.
     */
    suffix?: string;
}

/**
 * A custom MenuItem component that extends MUI's MenuItem with additional prefix and suffix icon capabilities.
 *
 * @component
 * @param {Object} props - The properties that define the MenuItem.
 * @param {string} [props.prefix] - The name of the icon to be displayed as a prefix.
 * @param {React.ReactNode} props.children - The content to be displayed in the MenuItem.
 * @param {string} [props.suffix] - The name of the icon to be displayed as a suffix.
 * @param {React.MouseEventHandler<HTMLElement>} [props.onClick] - The function to be called when the MenuItem is clicked.
 * @param {SxProps} [props.sx] - The system prop that allows defining system overrides and additional CSS styles.
 * @param {Omit<MenuItemProps, 'prefix' | 'suffix'>} [props...rest] - Additional props to be spread to the underlying MUI MenuItem component.
 *
 * @returns {JSX.Element} A customized MenuItem component with optional prefix and suffix icons.
 *
 * @example
 * <MenuItem
 *   prefix="IconName"
 *   suffix="AnotherIconName"
 *   onClick={handleClick}
 *   sx={{ color: 'primary.main' }}
 * >
 *   Menu Item Text
 * </MenuItem>
 */
export const MenuItem = ({
    prefix,
    children,
    suffix,
    onClick,
    sx,
    ...rest
}: EnhancedMenuItemProps): JSX.Element => (
    <MUIMenuItem
        onClick={onClick}
        {...rest}
        // Control the prefix and suffix alignment
        sx={{
            '&:hover': {
                backgroundColor: 'menuItem.hoverBackgroundColor'
            },
            alignItems: 'center',
            display: 'flex',
            paddingLeft: '24px',
            paddingRight: '16px',
            paddingY: '4px',
            ...sx
        }}
    >
        {prefix && (
            <IconWithVariant
                src={prefix as IconName}
                size={FontSizes[1]}
                wrapperProps={{ marginRight: '8px' }}
            />
        )}
        <Typography component="span" sx={{ flex: 1 }}>
            {children}
        </Typography>
        {suffix && (
            <IconWithVariant
                src={suffix as IconName}
                size={FontSizes[1]}
                wrapperProps={{ marginLeft: '16px' }}
            />
        )}
    </MUIMenuItem>
);

/**
 * A customizable dropdown menu component that supports nested submenus.
 *
 * @component
 * @param {Object} props - The properties that define the Menu component.
 * @param {Item[]} props.menu - An array of menu items to be displayed.
 * @param {(keep: boolean) => void} [props.keepParent] - Optional callback to control the visibility of the parent menu.
 * @param {Object} [props.MenuListProps] - Props to be passed to the underlying MenuList component.
 * @param {(event: React.MouseEvent<HTMLElement>, reason: 'backdropClick' | 'escapeKeyDown') => void} props.onClose - Callback fired when the menu is closed.
 * @param {...MenuProps} props.menuProps - Additional props to be spread to the underlying MUI Menu component.
 *
 * @example
 * const menuItems = [
 *   { type: 'header', labelKey: 'menu.header' },
 *   { type: 'leaf', labelKey: 'menu.item1', action: () => console.log('Item 1 clicked') },
 *   { type: 'submenu', labelKey: 'menu.submenu', submenu: [...] }
 * ];
 *
 * <Menu
 *   menu={menuItems}
 *   onClose={handleClose}
 *   anchorEl={anchorEl}
 *   open={Boolean(anchorEl)}
 * />
 */
export const Menu = ({
    menu,
    keepParent,
    MenuListProps,
    onClose,
    ...menuProps
}: EnhancedMenuProps) => {
    const [[anchorElLabel, anchorEl] = [], setAnchorEl] = useState<
        [string, HTMLElement] | undefined
    >();
    const { t } = useTranslation();

    const [keep, setKeep] = useState<string | undefined>();

    return (
        <MUIMenu
            onClose={onClose}
            MenuListProps={{
                ...MenuListProps,
                sx: { ...MenuListProps?.sx, minWidth: '160px', padding: 0 }
            }}
            slotProps={{
                paper: { sx: { paddingX: 0, paddingY: '8px' } }
            }}
            {...menuProps}
        >
            {menu.map((item) => {
                switch (item.type) {
                    case 'header':
                        return (
                            <MenuItem
                                key={item.labelKey}
                                // We remove any submenu that might be open
                                onMouseEnter={() => setAnchorEl(undefined)}
                                // Prevents the header from being clickable
                                sx={{
                                    paddingX: '16px',
                                    paddingY: '8px',
                                    pointerEvents: 'none'
                                }}
                            >
                                <Typography fontWeight={600}>
                                    {t(item.labelKey)}
                                </Typography>
                            </MenuItem>
                        );
                    case 'submenu':
                        return (
                            <MenuList key={item.labelKey} sx={{ padding: 0 }}>
                                <MenuItem
                                    data-trackid={item.dataTrackid}
                                    disabled={!item.submenu?.length}
                                    onMouseEnter={(event) => {
                                        setAnchorEl([
                                            item.labelKey,
                                            event.currentTarget
                                        ]);
                                    }}
                                    prefix={item.prefix}
                                    sx={{ paddingY: '8px' }}
                                    suffix="ChevronRight"
                                >
                                    {t(item.labelKey)}
                                </MenuItem>
                                {item.divider && (
                                    <Divider
                                        sx={{ borderColor: 'border.primary' }}
                                    />
                                )}
                                <Menu
                                    anchorEl={
                                        anchorElLabel === item.labelKey
                                            ? anchorEl
                                            : undefined
                                    }
                                    anchorOrigin={{
                                        horizontal: 'right',
                                        vertical: 'top'
                                    }}
                                    keepParent={(set) =>
                                        setKeep(set ? item.labelKey : undefined)
                                    }
                                    menu={item.submenu!}
                                    // We add controls to visual menu container
                                    MenuListProps={{
                                        'aria-label': `dropdown.submenu.${item.labelKey}`,
                                        onMouseEnter: () => {
                                            if (keepParent) {
                                                keepParent(true);
                                            }
                                        },
                                        onMouseLeave: () => {
                                            if (keep !== item.labelKey) {
                                                setAnchorEl(undefined);
                                                if (keepParent) {
                                                    keepParent(false);
                                                }
                                            }
                                        },
                                        // We bring back the pointer events in the submenu
                                        style: {
                                            pointerEvents: 'auto'
                                        }
                                    }}
                                    onClose={(event, reason) => {
                                        setAnchorEl(undefined);
                                        onClose(event, reason);
                                    }}
                                    open={anchorElLabel === item.labelKey}
                                    // We disable pointer events for the submenu overlay
                                    style={{
                                        pointerEvents: 'none'
                                    }}
                                    transformOrigin={{
                                        horizontal: 'left',
                                        vertical: 'top'
                                    }}
                                />
                            </MenuList>
                        );
                    case 'leaf':
                        return (
                            <MenuList key={item.labelKey} sx={{ padding: 0 }}>
                                <MenuItem
                                    data-trackid={item.dataTrackid}
                                    disabled={!item.action || item.disabled}
                                    key={item.labelKey}
                                    onClick={
                                        item.action
                                            ? (event) => {
                                                  item.action!(event);
                                                  setAnchorEl(undefined);
                                                  onClose(
                                                      event,
                                                      'backdropClick'
                                                  );
                                              }
                                            : undefined
                                    }
                                    // We remove any submenu that might be open
                                    onMouseEnter={() => setAnchorEl(undefined)}
                                    prefix={item.prefix}
                                    sx={{
                                        paddingX: '24px',
                                        paddingY: '8px'
                                    }}
                                    suffix={item.suffix}
                                >
                                    {t(item.labelKey)}
                                </MenuItem>
                                {item.divider && (
                                    <Divider
                                        sx={{ borderColor: 'border.primary' }}
                                    />
                                )}
                            </MenuList>
                        );
                    default:
                        return null;
                }
            })}
        </MUIMenu>
    );
};
